import { Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { MapPin, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { buildBreadcrumbLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import RegionMap from "../components/RegionMap";
import { regions, nearbyTowns } from "../data/regions";
import { localPages } from "../data/localPages";
import { api } from "../lib/api";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Einsatzgebiete", to: "/einsatzgebiete" },
];

export default function Einsatzgebiete() {
  const [cities, setCities] = useState([]);
  useEffect(() => {
    api.get("/cities/published").then((r) => setCities(r.data)).catch(() => setCities([]));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    cities.forEach((c) => {
      const key = c.district || "Weitere Orte";
      (g[key] = g[key] || []).push(c);
    });
    return Object.entries(g)
      .map(([district, list]) => [district, list.sort((a, b) => a.name.localeCompare(b.name, "de"))])
      .sort((a, b) => b[1].length - a[1].length);
  }, [cities]);

  return (
    <div data-testid="page-einsatzgebiete">
      <Seo
        path="/einsatzgebiete"
        title="Einsatzgebiete | Entrümpelung Ulm, Neu-Ulm, Illertissen, Allgäu | Rümpeltaxi Altenstadt"
        description="Einsatzgebiete von Rümpeltaxi aus Altenstadt (Iller): Ulm, Neu-Ulm, Illertissen, Biberach, Heidenheim, Göppingen, Memmingen und Allgäu. Weitere Orte auf Anfrage. Tel. 0151 70004440."
        jsonLd={buildBreadcrumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker="Einsatzgebiete"
        title="Regional und überregional im Einsatz"
        lead="Rümpeltaxi ist regional und überregional tätig. Unsere Schwerpunkte liegen rund um Ulm, Neu-Ulm, Illertissen, das Allgäu, Biberach, Heidenheim, Göppingen, Stuttgart, Ludwigsburg, Bietigheim-Bissingen und Heilbronn. Auch Anfragen aus weiteren Städten und Gemeinden prüfen wir individuell."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-page">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <RegionMap />
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">
                Schwerpunkte als Orientierung – keine feste Grenze
              </h2>
              <p className="mt-4 text-base leading-relaxed text-mid-gray">
                Die dargestellten Regionen dienen als Orientierung. Je nach Auftragsumfang sind auch
                Einsätze über diese Gebiete hinaus möglich. Unser Sitz ist in Altenstadt – wir
                unterhalten in den Einsatzregionen keine eigenen Niederlassungen.
              </p>
              <p className="mt-4 text-base leading-relaxed text-mid-gray" data-testid="einsatz-hinweis-hannover">
                Rümpeltaxi aus Altenstadt in Bayern – nicht das Unternehmen gleichen Namens in Hannover.
              </p>
              <Link
                to="/kontakt"
                className="mt-6 inline-flex items-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
                data-testid="einsatz-cta"
              >
                Einsatz in Ihrer Region anfragen <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <h2 className="mt-16 font-heading text-2xl font-bold text-anthracite md:text-3xl">
            Unsere Schwerpunktregionen
          </h2>
          <StaggerGroup className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {regions.map((r) => (
              <motion.div key={r.slug} variants={itemVariants}>
                <Link
                  to={`/einsatzgebiete/${r.slug}`}
                  className="group flex h-full flex-col rounded-sm border border-light-gray bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  data-testid={`region-card-${r.slug}`}
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-light-gray text-anthracite transition-colors group-hover:bg-yellow">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <h2 className="mt-4 font-heading text-xl font-bold text-anthracite">{r.name}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-mid-gray">{r.intro.slice(0, 110)}…</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-heading text-sm font-bold text-anthracite">
                    Region ansehen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </StaggerGroup>
          <Reveal className="mt-10">
            <p className="text-base text-mid-gray">
              Ihr Ort ist nicht dabei?{" "}
              <Link to="/kontakt" className="font-semibold text-anthracite underline hover:text-yellow-dark">
                Senden Sie uns Ihre Anfrage
              </Link>{" "}
              – Einsätze in weiteren Städten und Gemeinden prüfen wir gerne individuell.
            </p>
          </Reveal>

          <div className="mt-16" data-testid="nearby-towns">
            <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">
              Orte im Umkreis von 30 km
            </h2>
            <p className="mt-3 max-w-2xl text-base text-mid-gray">
              Rund um unseren Sitz in Altenstadt an der Iller sind wir in diesen Städten und Gemeinden
              regelmäßig im Einsatz – jeder Ort führt zur passenden Regionsseite.
            </p>
            <ul className="mt-6 columns-2 gap-x-8 text-base text-mid-gray sm:columns-3 lg:columns-4">
              {nearbyTowns.map((t) => (
                <li key={t.name} className="mb-2">
                  <Link
                    to={`/einsatzgebiete/${t.region}`}
                    className="hover:text-anthracite hover:underline"
                    data-testid={`nearby-town-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-16" data-testid="local-page-links">
            <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">
              Entrümpelung und Haushaltsauflösung rund um Ulm und Neu-Ulm
            </h2>
            <p className="mt-3 max-w-2xl text-base text-mid-gray">
              Ausführliche Informationen zu unseren Einsätzen in Ulm, Neu-Ulm, Illertissen und
              Altenstadt – Ablauf, Besonderheiten vor Ort und Antworten auf häufige Fragen.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {localPages.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  className="group inline-flex items-center gap-1.5 rounded-sm border border-light-gray bg-white px-3 py-2 text-sm font-semibold text-anthracite transition-colors hover:border-yellow hover:bg-light-gray"
                  data-testid={`local-page-link-${p.slug}`}
                >
                  <MapPin className="h-3.5 w-3.5 text-yellow" /> {p.h1}
                </Link>
              ))}
            </div>
          </div>

          {cities.length > 0 && (
            <div className="mt-16" data-testid="city-links">
              <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">
                Entrümpelung in Ihrer Stadt
              </h2>
              <p className="mt-3 max-w-2xl text-base text-mid-gray">
                Wir sind in über {cities.length} Städten und Gemeinden im Einsatz – hier finden Sie die
                passende Seite mit Informationen zu Entrümpelung, Haushaltsauflösung, Räumung, Umzug und
                Entsorgung vor Ort.
              </p>
              <div className="mt-8 space-y-8">
                {grouped.map(([district, list]) => (
                  <div key={district} data-testid={`district-group-${district}`}>
                    <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">
                      {district} <span className="text-mid-gray/50">({list.length})</span>
                    </h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {list.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/entruempelung/${c.slug}`}
                          className="group inline-flex items-center gap-1.5 rounded-sm border border-light-gray bg-white px-3 py-2 text-sm font-semibold text-anthracite transition-colors hover:border-yellow hover:bg-light-gray"
                          data-testid={`city-link-${c.slug}`}
                        >
                          <MapPin className="h-3.5 w-3.5 text-yellow" /> {c.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
